import { ASSETS_GRID_SCROLLER_ID, features } from '@/lib/config'
import styles from '@/components/assets-explorer/assets-explorer.module.scss'

export const GRID_REVEAL_DURATION_MS = 360
const GRID_PREVIEW_RESUME_DELAY_MS = 500
export const ASSETS_GRID_NAVIGATION_EVENT = 'assets:grid-navigation-start'

type NavigationTransition = {
  id: number
  destination?: string
  stage: 'loading' | 'revealing'
  overlay: HTMLDivElement | null
  pausedVideos: HTMLVideoElement[]
  timeout: number
}

let transition: NavigationTransition | undefined
let nextTransitionId = 0
let delayedResumeVideos: HTMLVideoElement[] = []
let delayedResumeTimeout: number | undefined

function locationKey(href: string) {
  const url = new URL(href, window.location.href)
  return `${url.pathname.replace(/\/$/, '')}${url.search}`
}

export function getGridNavigationTransition() {
  return transition
}

export function hasReachedGridDestination() {
  return !!transition?.destination && transition.destination === locationKey(window.location.href)
}

function clearTransition() {
  if (!transition) return
  const shouldResumeAfterLoadFailure = transition.stage === 'loading'
  const pausedVideos = transition.pausedVideos
  window.clearTimeout(transition.timeout)
  transition.overlay?.remove()
  if (shouldResumeAfterLoadFailure) resumeVideos(pausedVideos)
  delete document.documentElement.dataset.assetsGridNavigation
  transition = undefined
  window.dispatchEvent(new Event(ASSETS_GRID_NAVIGATION_EVENT))
}

export function cancelGridNavigationTransition() {
  const pendingVideos = takeDelayedResumeVideos()
  clearTransition()
  resumeVideos(pendingVideos)
}

function pauseGridVideos() {
  const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('[data-slot="video-preview"]'))
  const playingVideos = videos.filter((video) => !video.paused)
  playingVideos.forEach((video) => video.pause())
  return playingVideos
}

function resumeVideos(videos: HTMLVideoElement[]) {
  videos.forEach((video) => {
    if (video.isConnected) void video.play().catch(() => {})
  })
}

function takeDelayedResumeVideos() {
  if (delayedResumeTimeout !== undefined) window.clearTimeout(delayedResumeTimeout)
  delayedResumeTimeout = undefined
  const videos = delayedResumeVideos
  delayedResumeVideos = []
  return videos
}

function scheduleVideoResume(videos: HTMLVideoElement[]) {
  if (!videos.length) return
  const pendingVideos = takeDelayedResumeVideos()
  delayedResumeVideos = [...pendingVideos, ...videos.filter((video) => !pendingVideos.includes(video))]
  delayedResumeTimeout = window.setTimeout(() => {
    resumeVideos(delayedResumeVideos)
    delayedResumeVideos = []
    delayedResumeTimeout = undefined
  }, GRID_REVEAL_DURATION_MS + GRID_PREVIEW_RESUME_DELAY_MS)
}

function captureThumbnails() {
  const scroller = document.getElementById(ASSETS_GRID_SCROLLER_ID)
  if (!scroller) return null
  const viewport = scroller.getBoundingClientRect()
  const overlay = document.createElement('div')
  overlay.className = styles.assetGridSnapshot
  overlay.dataset.assetsGridSnapshot = ''
  overlay.dataset.phase = 'capturing'
  overlay.setAttribute('aria-hidden', 'true')
  overlay.inert = true
  overlay.style.clipPath = `inset(${Math.max(0, viewport.top)}px ${Math.max(0, window.innerWidth - viewport.right)}px ${Math.max(0, window.innerHeight - viewport.bottom)}px ${Math.max(0, viewport.left)}px)`

  const cards = document.querySelectorAll<HTMLElement>('[data-grid-asset-key]')
  for (const card of cards) {
    if (overlay.childElementCount >= 48) break
    const bounds = card.getBoundingClientRect()
    if (bounds.bottom <= viewport.top || bounds.top >= viewport.bottom || !bounds.width || !bounds.height) continue
    const image = card.querySelector<HTMLImageElement>('img')
    if (!image?.complete || !image.naturalWidth || getComputedStyle(card).visibility === 'hidden') continue

    const tile = document.createElement('div')
    tile.dataset.assetsGridSnapshotCard = ''
    Object.assign(tile.style, {
      left: `${bounds.left}px`,
      top: `${bounds.top}px`,
      width: `${bounds.width}px`,
      height: `${bounds.height}px`,
      backgroundColor: getComputedStyle(card).backgroundColor,
    })
    const thumbnail = new Image()
    thumbnail.src = image.currentSrc || image.src
    thumbnail.alt = ''
    tile.append(thumbnail)
    overlay.append(tile)
  }

  if (!overlay.childElementCount) return null
  scroller.append(overlay)
  return overlay
}

export function beginGridNavigationTransition(href?: string) {
  if (typeof window === 'undefined' || !features.compactCategoryNavigation) return
  const destination = href ? locationKey(href) : undefined
  if (destination === locationKey(window.location.href)) return
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  const overlay = transition?.overlay ?? captureThumbnails()
  const pausedVideos = [...(transition?.pausedVideos ?? []), ...takeDelayedResumeVideos()]
  pausedVideos.push(...pauseGridVideos().filter((video) => !pausedVideos.includes(video)))
  if (transition) window.clearTimeout(transition.timeout)
  const id = ++nextTransitionId
  transition = {
    id,
    destination,
    stage: 'loading',
    overlay,
    pausedVideos,
    timeout: window.setTimeout(clearTransition, 15_000),
  }
  if (overlay) {
    requestAnimationFrame(() => {
      if (transition?.id === id && transition.stage === 'loading') overlay.dataset.phase = 'loading'
    })
  }
  document.documentElement.dataset.assetsGridNavigation = 'loading'
  window.dispatchEvent(new Event(ASSETS_GRID_NAVIGATION_EVENT))
}

export function revealGridNavigationTransition(id: number) {
  if (!transition || transition.id !== id || transition.stage !== 'loading') return false
  window.clearTimeout(transition.timeout)
  transition.stage = 'revealing'
  scheduleVideoResume(transition.pausedVideos)
  transition.pausedVideos = []
  if (transition.overlay) transition.overlay.dataset.phase = 'revealing'
  document.documentElement.dataset.assetsGridNavigation = 'revealing'
  transition.timeout = window.setTimeout(clearTransition, GRID_REVEAL_DURATION_MS)
  window.dispatchEvent(new Event(ASSETS_GRID_NAVIGATION_EVENT))
  return true
}
